import { Injectable } from '@angular/core';
import { MarketplaceApp } from '@galaxy/marketplace-apps/v1';
import { createEmptyValue } from '../diff-utils';
import {
  ChangeTypeAddition,
  ChangeTypeDeletion,
  ChangedField,
  MarketplaceAppDiffModel,
  MpAppFieldDisplayType,
} from './marketplace-app-diff-model';

@Injectable()
export class MarketplaceAppDiffService {
  private INTEGRATION_SETTINGS_FIELD = 'integration';
  private END_USER_SETTINGS_FIED = 'endUserMarketing';
  private RESELLER_SETTINGS_FIELD = 'resellerMarketing';
  private ORDER_FORM_SETTINGS_FIELD = 'orderForm';
  private UPDATED_FIELD = 'updated';
  private COST_FIELD = 'cost';

  private fieldDisplayTypeMapping: Map<string, MpAppFieldDisplayType> = new Map([
    ['partnerContactRecipients', MpAppFieldDisplayType.Text],
    ['price', MpAppFieldDisplayType.Money],
    ['managementFee', MpAppFieldDisplayType.Money],
    ['suggestedRetailSetupFee', MpAppFieldDisplayType.Money],
    ['icon', MpAppFieldDisplayType.Image],
    ['screenshot', MpAppFieldDisplayType.Image],
    ['headerImage', MpAppFieldDisplayType.Image],
    ['created', MpAppFieldDisplayType.DateTime],
    ['dateChanged', MpAppFieldDisplayType.DateTime],
    ['signInUrl', MpAppFieldDisplayType.Url],
    ['websiteUrl', MpAppFieldDisplayType.Url],
    ['purchaseWebhookUrl', MpAppFieldDisplayType.Url],
    ['addonPurchaseWebhookUrl', MpAppFieldDisplayType.Url],
    ['userModWebhookUrl', MpAppFieldDisplayType.Url],
    ['salestoolWebhookUrl', MpAppFieldDisplayType.Url],
    ['accountModWebhookUrl', MpAppFieldDisplayType.Url],
    ['contactWebhookUrl', MpAppFieldDisplayType.Url],
    ['logoutWebhookUrl', MpAppFieldDisplayType.Url],
    ['entryUrl', MpAppFieldDisplayType.Url],
    ['sessionUrl', MpAppFieldDisplayType.Url],
    ['settingsUrl', MpAppFieldDisplayType.Url],
    ['serviceChangeWebhookUrl', MpAppFieldDisplayType.Url],
    ['contactEmail', MpAppFieldDisplayType.Email],
    ['fulfillmentEmail', MpAppFieldDisplayType.Email],
    ['files', MpAppFieldDisplayType.File],
    ['faqs', MpAppFieldDisplayType.Faq],
    ['orderForm', MpAppFieldDisplayType.OrderForm],
    ['commonFormRequiredFields', MpAppFieldDisplayType.CommonFormRequiredFields],
    ['lmiCategories', MpAppFieldDisplayType.lmiCategories],
    ['restrictions', MpAppFieldDisplayType.restrictions],
  ]);

  /**
   * Calculates the differences between two Marketplace Apps. Intended to be used with draft and
   * published versions of the same app.
   * @param draft A version of the app that has unpublished changes pending (the "new" version).
   * @param published The version of the app that is currently published to the store (the "old"
   * version).
   * @param draftSuggestedRetailSetupFee The suggested retail setup fee for the draft version of the app.
   * @param publishedSuggestedRetailSetupFee The suggested retail setup fee for the published version of the app.
   * @returns {MarketplaceAppDiffModel} An object containing several arrays with the categorized differences between the
   * two versions.
   */
  getMarketPlaceAppDiffs(
    draft: MarketplaceApp,
    published: MarketplaceApp,
    draftSuggestedRetailSetupFee?: number,
    publishedSuggestedRetailSetupFee?: number,
  ): MarketplaceAppDiffModel {
    const diffModel = new MarketplaceAppDiffModel();
    const allFields: string[] = [];
    for (const field in published) {
      if (Object.prototype.hasOwnProperty.call(published, field)) {
        allFields.push(field);
      }
    }
    for (const field in draft) {
      if (Object.prototype.hasOwnProperty.call(draft, field) && allFields.indexOf(field) < 0) {
        allFields.push(field);
      }
    }
    for (let i = 0; i < allFields.length; i++) {
      const field = allFields[i];
      if (Object.prototype.hasOwnProperty.call(published, field)) {
        if (Object.prototype.hasOwnProperty.call(draft, field)) {
          if (field === this.INTEGRATION_SETTINGS_FIELD) {
            this.diffIntegrationFields(published[field], draft[field], field, diffModel);
          } else if (field === this.END_USER_SETTINGS_FIED) {
            this.diffEndUserFields(published[field], draft[field], field, diffModel);
          } else if (field === this.RESELLER_SETTINGS_FIELD) {
            this.diffResellerFields(published[field], draft[field], field, diffModel);
          } else if (field === this.ORDER_FORM_SETTINGS_FIELD) {
            this.diffOrderFormFields(published[field], draft[field], field, diffModel);
          } else if (field === this.UPDATED_FIELD) {
            // Just set the dateChanged date on the model using the date from the Draft - don't diff
            // it.
            diffModel.dateChanged = new Date(draft.updated);
          } else if (field === this.COST_FIELD) {
            this.diffCostFields(published[field], draft[field], field, diffModel);
          } else {
            const change = this.diffField(published[field], draft[field], field);
            if (change) {
              diffModel.generalSettingsChanges.push(change);
            }
          }
        } else {
          if (field !== 'inDevelopment') {
            // Field exists in published but not draft.
            const change = new ChangedField(
              field,
              this.getDisplayType(field),
              published[field],
              createEmptyValue(published[field]),
              ChangeTypeAddition,
            );
            if (field === 'trialSupport') {
              diffModel.integrationChanges.push(change);
            } else {
              diffModel.generalSettingsChanges.push(change);
            }
          }
        }
      } else {
        if (field !== 'inDevelopment') {
          // Field exists in draft but not published.
          const change = new ChangedField(
            field,
            this.getDisplayType(field),
            createEmptyValue(draft[field]),
            draft[field],
            ChangeTypeAddition,
          );
          if (field === 'trialSupport') {
            diffModel.integrationChanges.push(change);
          } else {
            diffModel.generalSettingsChanges.push(change);
          }
        }
      }
    }
    // Suggested retail setup fee is not part of the MarketplaceApp object so it needs to be compared separately.
    if (draftSuggestedRetailSetupFee !== publishedSuggestedRetailSetupFee) {
      const change = new ChangedField(
        'suggestedRetailSetupFee',
        MpAppFieldDisplayType.Text,
        publishedSuggestedRetailSetupFee || 0,
        draftSuggestedRetailSetupFee || 0,
        // Changes made to a primitive type field are considers additions
        ChangeTypeAddition,
      );
      diffModel.generalSettingsChanges.push(change);
      console.log('diffModel', diffModel);
    }
    return diffModel;
  }

  private diffIntegrationFields(
    oldObject: any,
    newObject: any,
    fieldName: string,
    diffModel: MarketplaceAppDiffModel,
  ): void {
    const changes = this.diffObject(oldObject, newObject);
    for (let i = 0; i < changes.length; i++) {
      const change = changes[i];
      if (change.fieldName !== 'approved') {
        diffModel.integrationChanges.push(change);
      }
    }
  }

  private diffOrderFormFields(
    oldObject: any,
    newObject: any,
    fieldName: string,
    diffModel: MarketplaceAppDiffModel,
  ): void {
    // if (oldObject[key])
    const changes = this.diffObject(oldObject, newObject);
    for (let i = 0; i < changes.length; i++) {
      const change = changes[i];
      if (change.fieldName === 'activationMessage') {
        diffModel.resellerChanges.push(change);
      } else {
        diffModel.orderFormChanges.push(change);
      }
    }
  }

  private diffResellerFields(
    oldObject: any,
    newObject: any,
    fieldName: string,
    diffModel: MarketplaceAppDiffModel,
  ): void {
    const changes = this.diffObject(oldObject, newObject);
    for (let i = 0; i < changes.length; i++) {
      const change = changes[i];
      diffModel.resellerChanges.push(change);
    }
  }

  private diffEndUserFields(
    oldObject: any,
    newObject: any,
    fieldName: string,
    diffModel: MarketplaceAppDiffModel,
  ): void {
    const changes = this.diffObject(oldObject, newObject);
    for (let i = 0; i < changes.length; i++) {
      const change = changes[i];
      diffModel.endUserChanges.push(change);
    }
  }

  private diffCostFields(oldObject: any, newObject: any, fieldName: string, diffModel: MarketplaceAppDiffModel): void {
    const changes = this.diffObject(oldObject, newObject);
    for (let i = 0; i < changes.length; i++) {
      const change = changes[i];
      diffModel.generalSettingsChanges.push(change);
    }
  }

  private getDisplayType(fieldName: string): MpAppFieldDisplayType {
    if (this.fieldDisplayTypeMapping.has(fieldName)) {
      return this.fieldDisplayTypeMapping.get(fieldName);
    }

    return MpAppFieldDisplayType.Text;
  }

  protected diffObject(oldObject: any, newObject: any): ChangedField[] {
    const changes = [];
    for (const field in oldObject) {
      if (Object.prototype.hasOwnProperty.call(oldObject, field)) {
        if (Object.prototype.hasOwnProperty.call(newObject, field)) {
          // If the object has a nested array check each field in it for changes.
          if (oldObject[field] !== null && typeof oldObject[field] === 'object') {
            const oldField: Array<any> = oldObject[field];
            const newField: Array<any> = newObject[field];
            // Arrays should be considered equal if they are the same length and have the same items
            // in the same order.
            let length = 0;
            if (oldField.length === newField.length) {
              length = oldField.length;
            } else if (oldField.length > newField.length) {
              length = oldField.length;
            } else {
              length = newField.length;
            }
            for (let i = 0; i < length; i++) {
              // compare all values in the oldValues to all values in new values if it's array of
              // strings
              if (
                !(oldField[i] !== null && typeof oldField[i] === 'object') &&
                !(newField[i] !== null && typeof newField[i] === 'object')
              ) {
                if (newField[i]) {
                  if (!oldField.includes(newField[i])) {
                    let value = ' ';
                    if (oldField[i]) {
                      value = oldField[i];
                    }
                    changes.push(
                      new ChangedField(field, this.getDisplayType(field), value, newField[i], ChangeTypeAddition),
                    );
                    continue;
                  }
                }
                if (oldField[i]) {
                  if (!newField.includes(oldField[i])) {
                    let value = ' ';
                    if (newField[i]) {
                      value = newField[i];
                    }
                    changes.push(
                      new ChangedField(field, this.getDisplayType(field), oldField[i], value, ChangeTypeDeletion),
                    );
                  }
                }
              } else {
                const change = this.diffField(oldField[i], newField[i], field);
                if (change) {
                  changes.push(change);
                }
              }
            }
          } else {
            const change = this.diffField(oldObject[field], newObject[field], field);
            if (change) {
              changes.push(change);
            }
          }
        } else {
          // Field exists on oldObject but not newObject.
          if (Array.isArray(oldObject[field])) {
            newObject[field] = [];
            for (let i = 0; i < oldObject[field].length; i++) {
              const change = this.diffField(oldObject[field][i], newObject[field][i], field);
              if (change) {
                changes.push(change);
              }
            }
          } else {
            const change = this.diffField(oldObject[field], '', field);
            if (change) {
              changes.push(change);
            }
          }
        }
      }
    }

    for (const field in newObject) {
      if (Object.prototype.hasOwnProperty.call(newObject, field)) {
        if (!Object.prototype.hasOwnProperty.call(oldObject, field)) {
          // Field exists on newObject but not oldObject.
          if (Array.isArray(newObject[field])) {
            oldObject[field] = [''];
          } else {
            oldObject[field] = '';
          }
          const change = this.diffField(oldObject[field], newObject[field], field);
          if (change) {
            changes.push(change);
          }
        }
      }
    }

    return changes;
  }

  /**
   * Compares two fields and returns a ChangedField object if they are different.
   * @param oldField: The field from the published version of the app.
   * @param newField: The field from the draft version of the app.
   * @param fieldName: The name of the field being compared.
   */
  private diffField(oldField: any, newField: any, fieldName: string): ChangedField {
    if (!oldField && !newField) {
      return null;
    }
    if (oldField && !newField) {
      return new ChangedField(
        fieldName,
        this.getDisplayType(fieldName),
        oldField,
        createEmptyValue(oldField),
        ChangeTypeDeletion,
      );
    }
    if (newField && !oldField) {
      return new ChangedField(
        fieldName,
        this.getDisplayType(fieldName),
        createEmptyValue(newField),
        newField,
        ChangeTypeAddition,
      );
    }

    let areSame = true;
    let change = '';
    if (Array.isArray(oldField)) {
      // Arrays should be considered equal if they are the same length and have the same items in
      // the same order.
      let length = 0;
      if (oldField.length === newField.length) {
        length = oldField.length;
      } else if (oldField.length > newField.length) {
        length = oldField.length;
      } else {
        length = newField.length;
      }
      for (let i = 0; i < length && areSame; i++) {
        // Call itself recursively to compare each item in the array.
        const arrayChange = this.diffField(oldField[i], newField[i], fieldName);
        if (arrayChange) {
          areSame = false;
          change = arrayChange.changeType;
        }
      }
    } else if (oldField !== null && typeof oldField === 'object') {
      // Objects should be considered not equal if their JSON representations are not the same.
      if (JSON.stringify(oldField) === JSON.stringify(newField)) {
        areSame = true;
      } else if (JSON.stringify(oldField) > JSON.stringify(newField)) {
        change = ChangeTypeDeletion;
        areSame = false;
      } else {
        change = ChangeTypeAddition;
        areSame = false;
      }
      for (const field in oldField) {
        if (field !== 'toApiJson') {
          if (!Object.prototype.hasOwnProperty.call(newField, field)) {
            change = ChangeTypeDeletion;
            areSame = false;
            newField[field] = createEmptyValue(oldField[field]);
          }
        }
      }
      for (const field in newField) {
        if (!Object.prototype.hasOwnProperty.call(oldField, field)) {
          if (field !== 'toApiJson') {
            change = ChangeTypeAddition;
            areSame = false;
            oldField[field] = createEmptyValue(newField[field]);
          }
        }
      }
    } else {
      // Primitive types should be considered equal if they are the same value.
      // Will consider the change to be an addition if the values are different.
      if (oldField !== newField) {
        change = ChangeTypeAddition;
        areSame = false;
      }
    }
    if (!areSame) {
      return new ChangedField(fieldName, this.getDisplayType(fieldName), oldField, newField, change);
    }

    return null;
  }
}
